import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import { useAuthStore } from "@/store/auth-store";
import type { Membership, Organization } from "@/store/auth-store";

interface Paginated<T> {
  items: T[];
  count: number;
  limit: number;
  offset: number;
}

export function useOrganizations() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  return useQuery({
    queryKey: ["organizations"],
    queryFn: async () => {
      const page = await api.get("organizations/").json<Paginated<Organization>>();
      return page.items;
    },
    enabled: isAuthenticated,
  });
}

export function useOrganization(id: string) {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);

  return useQuery({
    queryKey: ["organizations", id],
    queryFn: () => api.get(`organizations/${id}/`).json<Organization>(),
    enabled: isAuthenticated && !!id,
  });
}

export interface ImageUrlSet {
  sm: string | null;
  md: string | null;
  lg: string | null;
  original: string | null;
}

export interface OrganizationSettings {
  guest_no_show_risk_threshold: number;
  logo_urls: ImageUrlSet | null;
}

export function useOrganizationSettings() {
  const isAuthenticated = useAuthStore((s) => s.isAuthenticated);
  const organizationId = useAuthStore((s) => s.organizationId);

  return useQuery({
    queryKey: ["organization-settings", organizationId],
    queryFn: () =>
      api.get(`organizations/${organizationId}/settings/`).json<OrganizationSettings>(),
    enabled: isAuthenticated && !!organizationId,
    staleTime: 5 * 60 * 1000,
  });
}

export function useSetDefaultMembership() {
  const queryClient = useQueryClient();
  const setOrganization = useAuthStore((s) => s.setOrganization);

  return useMutation({
    mutationFn: (membershipId: string) =>
      api.patch(`memberships/${membershipId}/default/`).json<Membership>(),
    onSuccess: (membership) => {
      setOrganization(membership.organization_id);
      queryClient.invalidateQueries({ queryKey: ["memberships"] });
    },
  });
}
